// Firebase Realtime Database integration for CallCenter Agent App
import { initializeApp } from "firebase/app";
import { getDatabase, ref, push, set } from "firebase/database";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAxcqXb9VOpCKWKYqFwjCyFnYuwiAt6XZU",
  authDomain: "webbey-e10fb.firebaseapp.com",
  projectId: "webbey-e10fb",
  storageBucket: "webbey-e10fb.firebasestorage.app",
  messagingSenderId: "501241657572",
  appId: "1:501241657572:web:3fa6f3171c16e4b81f94f1",
  measurementId: "G-T5JG3SSH4X",
  databaseURL:
    "https://webbey-e10fb-default-rtdb.europe-west1.firebasedatabase.app/",
};

// Initialize Firebase
let app = null;
let database = null;

try {
  app = initializeApp(firebaseConfig);
  database = getDatabase(app);
  console.log("Firebase Realtime Database initialized successfully");
} catch (error) {
  console.error("Error initializing Firebase:", error);
}

// Get user from localStorage (where AuthContext stores it)
const getUserFromStorage = () => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (error) {
    console.error("Failed to get user from localStorage:", error);
  }
  return null;
};

// Generate descriptive error ID: ErrorName_RandomId
const generateErrorId = (errorName) => {
  let randomId = Date.now().toString(36) + Math.random().toString(36).substr(2);

  // Try to get Firebase push ID if database is initialized
  if (database) {
    try {
      randomId = push(ref(database)).key || randomId;
    } catch (error) {
      console.error("Failed to generate Firebase push ID:", error);
    }
  }

  const cleanErrorName = (errorName || "UnknownError")
    .replace(/[^a-zA-Z0-9_]/g, "_") // Replace special chars with underscore
    .slice(0, 50); // Limit length
  return `${cleanErrorName}_${randomId}`;
};

// Log errors to Realtime Database - automatically gets user from localStorage
export const logErrorToCrashlytics = async (error, context = {}) => {
  try {
    const user = getUserFromStorage();

    // Always log to console first for immediate debugging
    console.error(
      "Application Error:",
      error,
      context,
      user ? `User: ${user.name}` : "No user",
    );

    if (!database) {
      console.error("Firebase Database not initialized");
      return null;
    }

    // Prepare user info
    const userInfo = user
      ? {
          userId: user.agentId || user.id || "unknown",
          userName: user.name || "unknown",
          userGroups: user.groups || [],
        }
      : null;

    // Handle different types of error objects
    let errorName = "UnknownError";
    let errorMessage = "";
    let errorStack = null;
    let errorCode = null;

    if (error instanceof Error) {
      // Standard Error object
      errorName = error.name || "Error";
      errorMessage = error.message || String(error);
      errorStack = error.stack || null;
      errorCode = error.code || null;
    } else if (error instanceof Event) {
      // Event object (e.g., from WebSocket.onerror)
      errorName = "WebSocketError";
      errorMessage = `WebSocket error event: ${error.type}`;
      errorStack = null;
      errorCode = null;

      // Extract additional event details
      if (error.target && error.target.readyState !== undefined) {
        const readyStateMap = {
          0: "CONNECTING",
          1: "OPEN",
          2: "CLOSING",
          3: "CLOSED",
        };
        errorMessage += `, readyState: ${readyStateMap[error.target.readyState] || error.target.readyState}`;
      }
    } else {
      // Other types (string, object, etc.)
      errorName = "UnknownError";
      errorMessage = String(error);
      errorStack = null;
      errorCode = null;
    }

    // Prepare error data with better structure
    const errorData = {
      timestamp: new Date().toISOString(),
      error: {
        name: errorName,
        message: errorMessage,
        stack: errorStack,
        code: errorCode,
      },
      context: {
        // Keep original context structure
        ...context,
        // Add user info to context
        user: userInfo,
      },
      environment: {
        userAgent: navigator?.userAgent || "Unknown",
        url: window.location.href,
        platform: navigator?.platform || "Unknown",
        language: navigator?.language || "Unknown",
        online: navigator?.onLine,
        screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      },
    };

    // Generate descriptive error ID
    const errorId = generateErrorId(error?.name);
    const errorRef = ref(database, `errors/${errorId}`);

    await set(errorRef, errorData);
    console.log(`Error logged to Firebase with ID: ${errorId}`, errorData);

    return errorId;
  } catch (dbError) {
    // If database write fails, still log to console
    console.error("Database write failed. Original error:", error, context);
    console.error("Database error:", dbError);
    return null;
  }
};

// Log WebSocket events to Realtime Database - automatically gets user from localStorage
export const trackWebSocketEvent = async (eventType, details = {}) => {
  try {
    const user = getUserFromStorage();

    // Always log to console first
    console.log(
      "WebSocket Event:",
      eventType,
      details,
      user ? `User: ${user.name}` : "No user",
    );

    if (!database) {
      console.error("Firebase Database not initialized");
      return null;
    }

    // Prepare user info
    const userInfo = user
      ? {
          userId: user.agentId || user.id || "unknown",
          userName: user.name || "unknown",
        }
      : null;

    // Prepare event data
    const eventData = {
      timestamp: new Date().toISOString(),
      eventType: eventType,
      details: {
        ...details,
        user: userInfo,
      },
      environment: {
        userAgent: navigator?.userAgent || "Unknown",
        url: window.location.href,
        platform: navigator?.platform || "Unknown",
      },
    };

    // Generate descriptive event ID: EventType_RandomId
    const randomId = push(ref(database)).key;
    const cleanEventType = eventType
      .replace(/[^a-zA-Z0-9_]/g, "_")
      .slice(0, 50);
    const eventId = `${cleanEventType}_${randomId}`;

    const eventsRef = ref(database, `websocket_events/${eventId}`);
    await set(eventsRef, eventData);

    console.log(
      `WebSocket event logged to Firebase with ID: ${eventId}`,
      eventData,
    );
    return eventId;
  } catch (error) {
    // If database write fails, still log to console
    console.error(
      "Database write failed for WebSocket event:",
      eventType,
      details,
    );
    console.error("Database error:", error);
    return null;
  }
};

// Export Firebase instances for direct use if needed
export { app, database };
